import { NavLink, Outlet, Navigate } from "react-router-dom";
import { useState } from "react";
import {
  LayoutDashboard, Users, ClipboardList, Wallet, Truck, BarChart3, Settings as SettingsIcon,
  Wrench, LogOut, Menu, X, KeyRound, CalendarDays,
} from "lucide-react";
import { useApp } from "../context/AppContext";
import { Button } from "./ui/button";
import { ChangePasswordDialog } from "./ChangePasswordDialog";
import { GlobalSearch } from "./GlobalSearch";

export default function Layout() {
  const { user, t, lang, setLang, currency, setCurrency, logout } = useApp();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-slate-400 font-heading font-semibold">ClearRoute…</div>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;

  const isAdmin = user.role === "admin";
  const nav = [
    { to: "/", icon: LayoutDashboard, label: t("dashboard"), end: true },
    { to: "/customers", icon: Users, label: t("customers") },
    { to: "/work-orders", icon: ClipboardList, label: t("workOrders") },
    { to: "/schedule", icon: CalendarDays, label: t("schedule") },
    { to: "/services", icon: Wrench, label: t("services") },
    ...(isAdmin
      ? [
          { to: "/financial", icon: Wallet, label: t("financial") },
          { to: "/suppliers", icon: Truck, label: t("suppliers") },
          { to: "/reports", icon: BarChart3, label: t("reports") },
          { to: "/settings", icon: SettingsIcon, label: t("settings") },
        ]
      : []),
  ];

  const sidebar = (
    <div className="flex flex-col h-full bg-slate-900">
      <div className="flex items-center gap-3 px-5 h-16 border-b border-slate-800">
        <img src="/brand-logo.jpg" alt="ClearRoute Auto Glass" data-testid="sidebar-logo"
          className="h-10 w-14 rounded-lg bg-white object-contain p-0.5" />
        <div>
          <div className="font-heading font-extrabold text-white leading-tight">ClearRoute</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Auto Glass</div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {nav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={`nav-${item.to === "/" ? "dashboard" : item.to.slice(1)}`}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors ${
                isActive ? "bg-sky-600 text-white" : "text-slate-300 hover:text-white hover:bg-slate-800"
              }`
            }
          >
            <item.icon size={18} strokeWidth={2} />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-4 py-4 border-t border-slate-800">
        <div className="text-sm text-white font-medium">{user.name}</div>
        <div className="text-xs text-slate-500 mb-3">{t(user.role)}</div>
        <Button
          data-testid="change-password-button"
          variant="outline"
          size="sm"
          className="w-full mb-2 bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
          onClick={() => setPwOpen(true)}
        >
          <KeyRound size={15} className="mr-2" /> {t("changePassword")}
        </Button>
        <Button
          data-testid="logout-button"
          variant="outline"
          size="sm"
          className="w-full bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
          onClick={logout}
        >
          <LogOut size={15} className="mr-2" /> {t("logout")}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside className="hidden lg:block w-64 fixed inset-y-0 left-0 z-30">{sidebar}</aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64">{sidebar}</aside>
        </div>
      )}
      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-6 sticky top-0 z-20">
          <div className="flex items-center gap-3 flex-1 min-w-0 mr-4">
            <button
              data-testid="mobile-menu-button"
              className="lg:hidden p-2 rounded-md hover:bg-slate-100"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <GlobalSearch />
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-md border border-slate-200 overflow-hidden text-xs font-semibold">
              <button
                data-testid="lang-en-button"
                className={`px-3 py-1.5 ${lang === "en" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                onClick={() => setLang("en")}
              >
                EN
              </button>
              <button
                data-testid="lang-pt-button"
                className={`px-3 py-1.5 ${lang === "pt" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                onClick={() => setLang("pt")}
              >
                PT
              </button>
            </div>
            <div className="flex rounded-md border border-slate-200 overflow-hidden text-xs font-semibold">
              <button
                data-testid="currency-usd-button"
                className={`px-3 py-1.5 ${currency === "USD" ? "bg-sky-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                onClick={() => setCurrency("USD")}
              >
                US$
              </button>
              <button
                data-testid="currency-brl-button"
                className={`px-3 py-1.5 ${currency === "BRL" ? "bg-sky-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                onClick={() => setCurrency("BRL")}
              >
                R$
              </button>
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-8 overflow-y-auto">
          <Outlet />
        </main>
      </div>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
}
